import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Plus, Users, Trash2, Building2, Mail, Database, Save, CreditCard, Send, Check, Loader2, Eye, EyeOff, FileText, Upload, Image, Download, Archive, RefreshCw, HardDrive, RotateCcw, Lock, Wallet, UserX, Clock, Play, BarChart3 } from "lucide-react";
import api from "../lib/api";
import { format } from "date-fns";
import { de } from "date-fns/locale";

export default function SettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [savingPension, setSavingPension] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "staff",
  });
  const [pensionSettings, setPensionSettings] = useState({
    pension_name: "",
    pension_address: "",
    pension_city: "",
    pension_postal_code: "",
    pension_phone: "",
    pension_email: "",
    bank_name: "",
    bank_iban: "",
    bank_bic: "",
    bank_account_holder: "",
    tax_number: "",
    // Microsoft Graph API Settings
    ms_graph_enabled: false,
    ms_graph_tenant_id: "",
    ms_graph_client_id: "",
    ms_graph_client_secret: "",
    ms_graph_sender_email: "",
    ms_graph_sender_name: "",
    ms_graph_reply_to_email: "",
    // Rapor Ayarları
    report_logo_data: null,
    report_letterhead_data: null,
    use_letterhead_for_kontoauszug: true,
    use_letterhead_for_reports: true,
    // Rapor Tasarım Ayarları
    report_show_payment_details: true,
    report_show_guest_list: true,
    report_show_bank_info: true,
    report_modern_style: true,
    kontoauszug_show_running_balance: true,
    kontoauszug_show_summary: true,
    // Metin Ayarları
    report_invoice_intro: "Sehr geehrte Damen und Herren,\n\nbitte überweisen Sie den folgenden Betrag auf unser Konto:",
    report_invoice_outro: "Mit freundlichen Grüßen",
    report_mahnung_1_text: "bei der Durchsicht unserer Unterlagen haben wir festgestellt, dass die folgende Zahlung noch nicht bei uns eingegangen ist.",
    report_mahnung_2_text: "trotz unserer ersten Zahlungserinnerung ist die folgende Forderung weiterhin offen.",
    report_mahnung_3_text: "dies ist unsere letzte Mahnung. Sollten Sie nicht innerhalb von 7 Tagen zahlen, werden wir rechtliche Schritte einleiten.",
  });
  const [savingReports, setSavingReports] = useState(false);
  // Backup State
  const [backupInfo, setBackupInfo] = useState(null);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [loadingBackupInfo, setLoadingBackupInfo] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedRestoreFile, setSelectedRestoreFile] = useState(null);
  // Reset Accounts State
  const [resetAccountsDialogOpen, setResetAccountsDialogOpen] = useState(false);
  const [resetAccountsPassword, setResetAccountsPassword] = useState("");
  const [resettingAccounts, setResettingAccounts] = useState(false);
  const [resetAccountsMode, setResetAccountsMode] = useState("all"); // "all" or "single"
  const [resetAccountsGuestId, setResetAccountsGuestId] = useState("");
  const [resetAccountsIncludePayments, setResetAccountsIncludePayments] = useState(true);
  const [guests, setGuests] = useState([]);
  // Reset Bewohner State
  const [resetBewohnerDialogOpen, setResetBewohnerDialogOpen] = useState(false);
  const [resetBewohnerPassword, setResetBewohnerPassword] = useState("");
  const [resettingBewohner, setResettingBewohner] = useState(false);
  // Scheduler State
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [triggeringRent, setTriggeringRent] = useState(false);
  // Delete Monthly Payments State
  const [deletePaymentsDialogOpen, setDeletePaymentsDialogOpen] = useState(false);
  const [deletePaymentsMonth, setDeletePaymentsMonth] = useState("");
  const [deletingPayments, setDeletingPayments] = useState(false);
  const [monthlyPaymentsSummary, setMonthlyPaymentsSummary] = useState([]);
  const [loadingMonthlyPayments, setLoadingMonthlyPayments] = useState(false);
  const [recalcMonth, setRecalcMonth] = useState("");
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user]);

  // Fetch guests for reset dialog
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        const res = await api.get("/guests");
        setGuests(res.data);
      } catch (error) {
        console.error("Failed to fetch guests:", error);
      }
    };
    fetchGuests();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch pension settings
      const settingsRes = await api.get("/settings/pension");
      setPensionSettings(settingsRes.data);
      
      // Fetch users if admin
      if (user?.role === "admin") {
        const usersRes = await api.get("/users");
        setUsers(usersRes.data);
        
        // Fetch backup info
        fetchBackupInfo();
        
        // Fetch scheduler status
        fetchSchedulerStatus();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await api.get("/system/scheduler-status");
      setSchedulerStatus(response.data);
      // Also fetch monthly payments summary
      fetchMonthlyPaymentsSummary();
    } catch (error) {
      console.error("Scheduler status error:", error);
    }
  };

  const handleTriggerAutoRent = async () => {
    setTriggeringRent(true);
    try {
      await api.post("/system/trigger-auto-rent");
      toast.success("Automatische Mietgenerierung ausgelöst");
      fetchSchedulerStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    } finally {
      setTriggeringRent(false);
    }
  };

  // Monthly Payments Management
  const fetchMonthlyPaymentsSummary = async () => {
    setLoadingMonthlyPayments(true);
    try {
      const res = await api.get("/system/monthly-payments-summary");
      setMonthlyPaymentsSummary(res.data.months || []);
    } catch (error) {
      console.error("Failed to fetch monthly payments:", error);
    } finally {
      setLoadingMonthlyPayments(false);
    }
  };

  const handleDeleteMonthlyPayments = async () => {
    if (!deletePaymentsMonth) {
      toast.error("Bitte wählen Sie einen Monat aus");
      return;
    }
    
    if (!window.confirm(`Sind Sie sicher, dass Sie alle Zahlungen für ${deletePaymentsMonth} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    
    setDeletingPayments(true);
    try {
      const res = await api.delete(`/system/delete-monthly-payments?month=${deletePaymentsMonth}`);
      toast.success(`${res.data.deleted_transactions} Buchungen und ${res.data.deleted_payments} Zahlungen gelöscht`);
      setDeletePaymentsDialogOpen(false);
      setDeletePaymentsMonth("");
      fetchMonthlyPaymentsSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    } finally {
      setDeletingPayments(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", formData);
      toast.success("Benutzer erfolgreich erstellt");
      setDialogOpen(false);
      setFormData({ email: "", password: "", name: "", role: "staff" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Möchten Sie diesen Benutzer wirklich löschen?")) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success("Benutzer erfolgreich gelöscht");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      toast.success("Rolle erfolgreich geändert");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Ändern der Rolle");
    }
  };

  const handleRecalculateMonth = async () => {
    if (!recalcMonth) return;
    if (!window.confirm(`Monat ${recalcMonth} wird neu berechnet. Alle bestehenden Auto-Mieten für diesen Monat werden gelöscht und neu erstellt. Fortfahren?`)) return;
    setRecalculating(true);
    try {
      const res = await api.post(`/system/recalculate-month?month=${recalcMonth}`);
      toast.success(`${recalcMonth}: ${res.data.generated} Mieten neu berechnet (${res.data.total_amount.toFixed(2)} EUR)`);
      fetchMonthlyPaymentsSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler bei Neuberechnung");
    } finally {
      setRecalculating(false);
    }
  };

  const handleSavePensionSettings = async (e) => {
    e.preventDefault();
    setSavingPension(true);
    try {
      await api.post("/settings/pension", pensionSettings);
      toast.success("Einstellungen erfolgreich gespeichert");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingPension(false);
    }
  };

  const handleSaveSmtpSettings = async (e) => {
    e.preventDefault();
    setSavingSmtp(true);
    try {
      await api.post("/settings/pension", pensionSettings);
      toast.success("SMTP-Einstellungen erfolgreich gespeichert");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error("Bitte geben Sie eine Test-E-Mail-Adresse ein");
      return;
    }
    setTestingEmail(true);
    try {
      await api.post("/email/test", { to_email: testEmail });
      toast.success(`Test-E-Mail wurde an ${testEmail} gesendet`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim E-Mail-Versand");
    } finally {
      setTestingEmail(false);
    }
  };

  // Logo yükleme
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Bitte wählen Sie eine Bilddatei aus");
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Die Datei darf maximal 2MB groß sein");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setPensionSettings(prev => ({
        ...prev,
        report_logo_data: event.target?.result
      }));
      toast.success("Logo hochgeladen");
    };
    reader.readAsDataURL(file);
  };

  // Antetli kağıt PDF yükleme
  const handleLetterheadUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error("Bitte wählen Sie eine PDF-Datei aus");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Die Datei darf maximal 5MB groß sein");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setPensionSettings(prev => ({
        ...prev,
        report_letterhead_data: event.target?.result
      }));
      toast.success("Briefpapier hochgeladen");
    };
    reader.readAsDataURL(file);
  };

  // Rapor ayarlarını kaydet
  const handleSaveReportSettings = async (e) => {
    e.preventDefault();
    setSavingReports(true);
    try {
      await api.post("/settings/pension", pensionSettings);
      toast.success("Berichtseinstellungen erfolgreich gespeichert");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingReports(false);
    }
  };

  // Backup fonksiyonları
  const fetchBackupInfo = async () => {
    setLoadingBackupInfo(true);
    try {
      const response = await api.get("/system/backup/info");
      setBackupInfo(response.data);
    } catch (error) {
      console.error("Backup info error:", error);
    } finally {
      setLoadingBackupInfo(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const response = await api.get("/system/backup", {
        responseType: 'blob'
      });
      
      // Download the file
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Pension_Backup_${new Date().toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Backup erfolgreich erstellt und heruntergeladen");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Backup fehlgeschlagen");
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestoreFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      toast.error("Bitte wählen Sie eine ZIP-Backup-Datei aus");
      return;
    }
    
    setSelectedRestoreFile(file);
    setRestoreDialogOpen(true);
  };

  const handleRestoreBackup = async () => {
    if (!selectedRestoreFile) return;
    
    setRestoringBackup(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedRestoreFile);
      
      await api.post("/system/restore", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("Backup erfolgreich wiederhergestellt! Seite wird neu geladen...");
      setRestoreDialogOpen(false);
      setSelectedRestoreFile(null);
      
      // Refresh page after restore
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Wiederherstellung fehlgeschlagen");
    } finally {
      setRestoringBackup(false);
    }
  };

  // Hesapları sıfırlama
  const handleResetAccounts = async () => {
    if (!resetAccountsPassword) {
      toast.error("Bitte geben Sie Ihr Passwort ein");
      return;
    }
    
    if (resetAccountsMode === "single" && !resetAccountsGuestId) {
      toast.error("Bitte wählen Sie einen Bewohner aus");
      return;
    }
    
    setResettingAccounts(true);
    try {
      const response = await api.post("/system/reset-konten", {
        password: resetAccountsPassword,
        guest_id: resetAccountsMode === "single" ? resetAccountsGuestId : null,
        include_payments: resetAccountsIncludePayments
      });
      
      toast.success(response.data.message);
      setResetAccountsDialogOpen(false);
      setResetAccountsPassword("");
      setResetAccountsMode("all");
      setResetAccountsGuestId("");
      setResetAccountsIncludePayments(true);
      fetchBackupInfo(); // Backup info'yu güncelle
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Falsches Passwort");
      } else {
        toast.error(error.response?.data?.detail || "Fehler beim Zurücksetzen");
      }
    } finally {
      setResettingAccounts(false);
    }
  };

  // Bewohner sıfırlama
  const handleResetBewohner = async () => {
    if (!resetBewohnerPassword) {
      toast.error("Bitte geben Sie Ihr Passwort ein");
      return;
    }
    
    setResettingBewohner(true);
    try {
      const response = await api.post("/system/reset-bewohner", {
        password: resetBewohnerPassword
      });
      
      toast.success(response.data.message);
      setResetBewohnerDialogOpen(false);
      setResetBewohnerPassword("");
      fetchBackupInfo();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Falsches Passwort");
      } else {
        toast.error(error.response?.data?.detail || "Fehler beim Zurücksetzen");
      }
    } finally {
      setResettingBewohner(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "admin":
        return <Badge className="bg-slate-900 text-white">Administrator</Badge>;
      case "manager":
        return <Badge className="bg-blue-600 text-white">Manager</Badge>;
      default:
        return <Badge variant="outline">Mitarbeiter</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="settings-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Einstellungen
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          System- und Benutzereinstellungen
        </p>
      </div>

      <Tabs defaultValue="pension">
        <TabsList className="w-full grid grid-cols-6 sm:w-auto sm:flex">
          <TabsTrigger value="pension" className="text-xs sm:text-sm">Pension</TabsTrigger>
          <TabsTrigger value="reports" className="text-xs sm:text-sm">Berichte</TabsTrigger>
          <TabsTrigger value="email" className="text-xs sm:text-sm">E-Mail</TabsTrigger>
          <TabsTrigger value="users" className="text-xs sm:text-sm">Benutzer</TabsTrigger>
          {user?.role === "admin" && (
            <TabsTrigger value="system" className="text-xs sm:text-sm">System</TabsTrigger>
          )}
          <TabsTrigger value="integrations" className="text-xs sm:text-sm">Integration</TabsTrigger>
        </TabsList>

        {/* Pension Settings Tab */}
        <TabsContent value="pension" className="space-y-4 sm:space-y-6">
          <form onSubmit={handleSavePensionSettings}>
            {/* Pension Info Card */}
            <Card className="border-slate-200 mb-4">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Building2 className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                  Pensionsinformationen
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Diese Daten erscheinen auf Rechnungen und Mahnungen
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pension_name">Name der Pension *</Label>
                    <Input
                      id="pension_name"
                      value={pensionSettings.pension_name}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, pension_name: e.target.value }))}
                      placeholder="z.B. Soziale Pension Nürnberg"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pension_phone">Telefon</Label>
                    <Input
                      id="pension_phone"
                      value={pensionSettings.pension_phone}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, pension_phone: e.target.value }))}
                      placeholder="+49 911 12345678"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pension_address">Adresse</Label>
                  <Input
                    id="pension_address"
                    value={pensionSettings.pension_address}
                    onChange={(e) => setPensionSettings((p) => ({ ...p, pension_address: e.target.value }))}
                    placeholder="Musterstraße 123"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pension_postal_code">PLZ</Label>
                    <Input
                      id="pension_postal_code"
                      value={pensionSettings.pension_postal_code}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, pension_postal_code: e.target.value }))}
                      placeholder="90402"
                    />
                  </div>
                  <div className="space-y-2 col-span-1 sm:col-span-2">
                    <Label htmlFor="pension_city">Stadt</Label>
                    <Input
                      id="pension_city"
                      value={pensionSettings.pension_city}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, pension_city: e.target.value }))}
                      placeholder="Nürnberg"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pension_email">E-Mail</Label>
                    <Input
                      id="pension_email"
                      type="email"
                      value={pensionSettings.pension_email}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, pension_email: e.target.value }))}
                      placeholder="info@pension.de"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tax_number">Steuernummer</Label>
                    <Input
                      id="tax_number"
                      value={pensionSettings.tax_number}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, tax_number: e.target.value }))}
                      placeholder="123/456/78901"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Info Card */}
            <Card className="border-slate-200 mb-4">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                  Bankverbindung
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Für Rechnungen und Zahlungserinnerungen
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_holder">Kontoinhaber</Label>
                    <Input
                      id="bank_account_holder"
                      value={pensionSettings.bank_account_holder}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, bank_account_holder: e.target.value }))}
                      placeholder="Soziale Pension GmbH"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Bank</Label>
                    <Input
                      id="bank_name"
                      value={pensionSettings.bank_name}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, bank_name: e.target.value }))}
                      placeholder="Sparkasse Nürnberg"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_iban">IBAN</Label>
                    <Input
                      id="bank_iban"
                      value={pensionSettings.bank_iban}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, bank_iban: e.target.value }))}
                      placeholder="DE89 3704 0044 0532 0130 00"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_bic">BIC</Label>
                    <Input
                      id="bank_bic"
                      value={pensionSettings.bank_bic}
                      onChange={(e) => setPensionSettings((p) => ({ ...p, bank_bic: e.target.value }))}
                      placeholder="SSKNDE77XXX"
                      className="font-mono"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button 
              type="submit" 
              className="w-full sm:w-auto"
              style={{ backgroundColor: "#c11718" }}
              disabled={savingPension}
            >
              <Save className="h-4 w-4 mr-2" />
              {savingPension ? "Wird gespeichert..." : "Einstellungen speichern"}
            </Button>
          </form>
        </TabsContent>

        {/* Berichte (Reports) Tab */}
        <TabsContent value="reports" className="space-y-4 sm:space-y-6">
          <form onSubmit={handleSaveReportSettings}>
            {/* Logo & Briefpapier Upload */}
            <Card className="border-slate-200 mb-4">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Image className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                  Logo & Briefpapier
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Logo und Briefpapier für Rechnungen, Mahnungen und Kontoauszüge
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-6">
                {/* Logo Upload */}
                <div className="space-y-3">
                  <Label>Logo (PNG, JPG - max. 2MB)</Label>
                  <div className="flex items-center gap-4">
                    {pensionSettings.report_logo_data ? (
                      <div className="relative">
                        <img 
                          src={pensionSettings.report_logo_data} 
                          alt="Logo" 
                          className="h-16 w-auto object-contain border rounded p-2 bg-white"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-red-100 hover:bg-red-200 text-red-600"
                          onClick={() => setPensionSettings(prev => ({ ...prev, report_logo_data: null }))}
                        >
                          ×
                        </Button>
                      </div>
                    ) : (
                      <div className="h-16 w-32 border-2 border-dashed border-slate-300 rounded flex items-center justify-center text-slate-400">
                        <Image className="h-6 w-6" />
                      </div>
                    )}
                    <div>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <Label htmlFor="logo-upload" className="cursor-pointer">
                        <Button type="button" variant="outline" size="sm" asChild>
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            Logo hochladen
                          </span>
                        </Button>
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Briefpapier PDF Upload */}
                <div className="space-y-3">
                  <Label>Briefpapier (PDF - max. 5MB)</Label>
                  <div className="flex items-center gap-4">
                    {pensionSettings.report_letterhead_data ? (
                      <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                        <FileText className="h-5 w-5 text-emerald-600" />
                        <span className="text-sm text-emerald-700">Briefpapier hochgeladen</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-600 hover:bg-red-100"
                          onClick={() => setPensionSettings(prev => ({ ...prev, report_letterhead_data: null }))}
                        >
                          ×
                        </Button>
                      </div>
                    ) : (
                      <div className="h-12 w-48 border-2 border-dashed border-slate-300 rounded flex items-center justify-center text-slate-400">
                        <FileText className="h-5 w-5 mr-2" />
                        <span className="text-xs">Kein Briefpapier</span>
                      </div>
                    )}
                    <div>
                      <Input
                        type="file"
                        accept="application/pdf"
                        onChange={handleLetterheadUpload}
                        className="hidden"
                        id="letterhead-upload"
                      />
                      <Label htmlFor="letterhead-upload" className="cursor-pointer">
                        <Button type="button" variant="outline" size="sm" asChild>
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            PDF hochladen
                          </span>
                        </Button>
                      </Label>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    Tipp: Das Briefpapier sollte im A4-Format sein und Platz für den Inhalt lassen.
                  </p>
                  
                  {/* Briefpapier Kullanım Seçenekleri */}
                  {pensionSettings.report_letterhead_data && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg space-y-3">
                      <p className="text-sm font-medium text-slate-700">Briefpapier verwenden für:</p>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pensionSettings.use_letterhead_for_kontoauszug}
                            onChange={(e) => setPensionSettings(prev => ({ 
                              ...prev, 
                              use_letterhead_for_kontoauszug: e.target.checked 
                            }))}
                            className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="text-sm text-slate-600">Kontoauszüge</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pensionSettings.use_letterhead_for_reports}
                            onChange={(e) => setPensionSettings(prev => ({ 
                              ...prev, 
                              use_letterhead_for_reports: e.target.checked 
                            }))}
                            className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="text-sm text-slate-600">Zahlungsberichte (Z-Bericht)</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Berichtgestaltung - Rapor Tasarımı */}
            <Card className="border-slate-200 mb-4">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                  Berichtgestaltung
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Anpassung der Inhalte für Finanzberichte und Kontoauszüge
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
                {/* Z-Bericht Ayarları */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-3">Finanzbericht (Z-Bericht)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pensionSettings.report_modern_style}
                        onChange={(e) => setPensionSettings(prev => ({ 
                          ...prev, 
                          report_modern_style: e.target.checked 
                        }))}
                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-slate-600">Modernes Design</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pensionSettings.report_show_payment_details}
                        onChange={(e) => setPensionSettings(prev => ({ 
                          ...prev, 
                          report_show_payment_details: e.target.checked 
                        }))}
                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-slate-600">Einzelpositionen anzeigen</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pensionSettings.report_show_guest_list}
                        onChange={(e) => setPensionSettings(prev => ({ 
                          ...prev, 
                          report_show_guest_list: e.target.checked 
                        }))}
                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-slate-600">Bewohner-Übersicht anzeigen</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pensionSettings.report_show_bank_info}
                        onChange={(e) => setPensionSettings(prev => ({ 
                          ...prev, 
                          report_show_bank_info: e.target.checked 
                        }))}
                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-slate-600">Bankverbindung anzeigen</span>
                    </label>
                  </div>
                </div>

                {/* Kontoauszug Ayarları */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-3">Kontoauszug</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pensionSettings.kontoauszug_show_running_balance}
                        onChange={(e) => setPensionSettings(prev => ({ 
                          ...prev, 
                          kontoauszug_show_running_balance: e.target.checked 
                        }))}
                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-slate-600">Laufenden Saldo anzeigen</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pensionSettings.kontoauszug_show_summary}
                        onChange={(e) => setPensionSettings(prev => ({ 
                          ...prev, 
                          kontoauszug_show_summary: e.target.checked 
                        }))}
                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-slate-600">Zusammenfassung anzeigen</span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rechnungstexte */}
            <Card className="border-slate-200 mb-4">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                  Rechnungstexte
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Einleitung und Schlusstext für Rechnungen
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="invoice_intro">Einleitungstext</Label>
                  <textarea
                    id="invoice_intro"
                    value={pensionSettings.report_invoice_intro}
                    onChange={(e) => setPensionSettings(prev => ({ ...prev, report_invoice_intro: e.target.value }))}
                    className="w-full h-24 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                    placeholder="Sehr geehrte Damen und Herren,..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invoice_outro">Schlusstext</Label>
                  <textarea
                    id="invoice_outro"
                    value={pensionSettings.report_invoice_outro}
                    onChange={(e) => setPensionSettings(prev => ({ ...prev, report_invoice_outro: e.target.value }))}
                    className="w-full h-16 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                    placeholder="Mit freundlichen Grüßen"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Mahnungstexte */}
            <Card className="border-slate-200 mb-4">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                  Mahnungstexte
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Texte für 1., 2. und 3. Mahnung
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mahnung_1" className="flex items-center gap-2">
                    <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded">1. Mahnung</span>
                    Zahlungserinnerung
                  </Label>
                  <textarea
                    id="mahnung_1"
                    value={pensionSettings.report_mahnung_1_text}
                    onChange={(e) => setPensionSettings(prev => ({ ...prev, report_mahnung_1_text: e.target.value }))}
                    className="w-full h-20 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mahnung_2" className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded">2. Mahnung</span>
                  </Label>
                  <textarea
                    id="mahnung_2"
                    value={pensionSettings.report_mahnung_2_text}
                    onChange={(e) => setPensionSettings(prev => ({ ...prev, report_mahnung_2_text: e.target.value }))}
                    className="w-full h-20 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mahnung_3" className="flex items-center gap-2">
                    <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded">3. Mahnung</span>
                    Letzte Mahnung
                  </Label>
                  <textarea
                    id="mahnung_3"
                    value={pensionSettings.report_mahnung_3_text}
                    onChange={(e) => setPensionSettings(prev => ({ ...prev, report_mahnung_3_text: e.target.value }))}
                    className="w-full h-20 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                  />
                </div>
              </CardContent>
            </Card>

            <Button 
              type="submit" 
              className="w-full sm:w-auto"
              style={{ backgroundColor: "#c11718" }}
              disabled={savingReports}
            >
              <Save className="h-4 w-4 mr-2" />
              {savingReports ? "Wird gespeichert..." : "Berichtseinstellungen speichern"}
            </Button>
          </form>
        </TabsContent>

        {/* Email/Microsoft Graph Tab */}
        <TabsContent value="email" className="space-y-4 sm:space-y-6">
          <form onSubmit={handleSaveSmtpSettings}>
            <Card className="border-slate-200 mb-4">
              <CardHeader className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <Mail className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                      Microsoft Graph API (Office 365)
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm mt-1">
                      E-Mail-Versand für Rechnungen und Mahnungen über Azure AD
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="ms-graph-enabled" className="text-sm">Aktiviert</Label>
                    <Switch
                      id="ms-graph-enabled"
                      checked={pensionSettings.ms_graph_enabled}
                      onCheckedChange={(checked) => setPensionSettings({...pensionSettings, ms_graph_enabled: checked})}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ms_graph_tenant_id">Verzeichnis-ID (Tenant ID)</Label>
                    <Input
                      id="ms_graph_tenant_id"
                      value={pensionSettings.ms_graph_tenant_id}
                      onChange={(e) => setPensionSettings({...pensionSettings, ms_graph_tenant_id: e.target.value})}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ms_graph_client_id">Anwendungs-ID (Client ID)</Label>
                    <Input
                      id="ms_graph_client_id"
                      value={pensionSettings.ms_graph_client_id}
                      onChange={(e) => setPensionSettings({...pensionSettings, ms_graph_client_id: e.target.value})}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ms_graph_client_secret">Geheimer Clientschlüssel</Label>
                    <div className="relative">
                      <Input
                        id="ms_graph_client_secret"
                        type={showPassword ? "text" : "password"}
                        value={pensionSettings.ms_graph_client_secret}
                        onChange={(e) => setPensionSettings({...pensionSettings, ms_graph_client_secret: e.target.value})}
                        placeholder="••••••••••••••••••••"
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ms_graph_sender_email">Absender E-Mail *</Label>
                    <Input
                      id="ms_graph_sender_email"
                      type="email"
                      value={pensionSettings.ms_graph_sender_email}
                      onChange={(e) => setPensionSettings({...pensionSettings, ms_graph_sender_email: e.target.value})}
                      placeholder="noreply@ihrefirma.de"
                    />
                    <p className="text-xs text-slate-500">Diese E-Mail muss in Ihrer Organisation existieren (zum Senden)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ms_graph_sender_name">Absendername</Label>
                    <Input
                      id="ms_graph_sender_name"
                      value={pensionSettings.ms_graph_sender_name}
                      onChange={(e) => setPensionSettings({...pensionSettings, ms_graph_sender_name: e.target.value})}
                      placeholder="Wohnheim Verwaltung"
                    />
                    <p className="text-xs text-slate-500">Name der im E-Mail als Absender angezeigt wird</p>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="ms_graph_reply_to_email">Antwort-E-Mail (Reply-To)</Label>
                    <Input
                      id="ms_graph_reply_to_email"
                      type="email"
                      value={pensionSettings.ms_graph_reply_to_email}
                      onChange={(e) => setPensionSettings({...pensionSettings, ms_graph_reply_to_email: e.target.value})}
                      placeholder="wohnheim@ihrefirma.de"
                    />
                    <p className="text-xs text-slate-500">Antworten werden an diese Adresse gesendet (optional)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Test Email */}
            <Card className="border-slate-200 mb-4">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Send className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                  Test E-Mail senden
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Überprüfen Sie die Microsoft Graph Konfiguration mit einer Test-E-Mail
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="flex gap-3">
                  <Input
                    type="email"
                    placeholder="test@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="max-w-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestEmail}
                    disabled={testingEmail || !pensionSettings.ms_graph_enabled}
                  >
                    {testingEmail ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sende...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Test senden
                      </>
                    )}
                  </Button>
                </div>
                {!pensionSettings.ms_graph_enabled && (
                  <p className="text-xs text-amber-600 mt-2">
                    Bitte aktivieren Sie Microsoft Graph, um Test-E-Mails zu senden.
                  </p>
                )}
              </CardContent>
            </Card>

            <Button type="submit" disabled={savingSmtp} style={{ backgroundColor: "#c11718" }}>
              {savingSmtp ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  E-Mail-Einstellungen speichern
                </>
              )}
            </Button>
          </form>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4 sm:space-y-6">
          {user?.role === "admin" ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Benutzerverwaltung
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-600">
                    Benutzerkonten verwalten
                  </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-user-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Neuer Benutzer
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md mx-4 sm:mx-auto">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Neuen Benutzer erstellen</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                          required
                          data-testid="user-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">E-Mail *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                          required
                          data-testid="user-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password">Passwort *</Label>
                        <Input
                          id="password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                          required
                          data-testid="user-password-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rolle *</Label>
                        <Select
                          value={formData.role}
                          onValueChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
                        >
                          <SelectTrigger data-testid="user-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="staff">Mitarbeiter</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="admin">Administrator</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                          Abbrechen
                        </Button>
                        <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-user-btn">
                          Erstellen
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card className="border-slate-200">
                <CardContent className="p-4 sm:pt-6">
                  {/* Mobile View - Cards */}
                  <div className="block sm:hidden space-y-3">
                    {users.map((u) => (
                      <div 
                        key={u.id} 
                        className="p-3 bg-slate-50 rounded-lg border border-slate-200"
                        data-testid={`user-card-${u.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{u.name}</div>
                            <div className="text-xs text-slate-500 truncate">{u.email}</div>
                          </div>
                          {getRoleBadge(u.role)}
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                          <span className="text-xs text-slate-500">
                            {format(new Date(u.created_at), "dd.MM.yy", { locale: de })}
                          </span>
                          {u.id !== user?.id && (
                            <div className="flex items-center gap-1">
                              <Select
                                value={u.role}
                                onValueChange={(value) => handleChangeRole(u.id, value)}
                              >
                                <SelectTrigger className="w-[110px] h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">Administrator</SelectItem>
                                  <SelectItem value="manager">Manager</SelectItem>
                                  <SelectItem value="staff">Mitarbeiter</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteUser(u.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop View - Table */}
                  <div className="hidden sm:block rounded-md border border-slate-200 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold text-slate-700">Name</TableHead>
                          <TableHead className="font-semibold text-slate-700">E-Mail</TableHead>
                          <TableHead className="font-semibold text-slate-700">Rolle</TableHead>
                          <TableHead className="font-semibold text-slate-700">Erstellt</TableHead>
                          <TableHead className="font-semibold text-slate-700 text-right">Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((u) => (
                          <TableRow key={u.id} className="hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                            <TableCell className="font-medium">{u.name}</TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>{getRoleBadge(u.role)}</TableCell>
                            <TableCell>
                              {format(new Date(u.created_at), "dd.MM.yyyy", { locale: de })}
                            </TableCell>
                            <TableCell className="text-right">
                              {u.id !== user?.id && (
                                <div className="flex items-center justify-end gap-1">
                                  <Select
                                    value={u.role}
                                    onValueChange={(value) => handleChangeRole(u.id, value)}
                                  >
                                    <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`role-select-${u.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="admin">Administrator</SelectItem>
                                      <SelectItem value="manager">Manager</SelectItem>
                                      <SelectItem value="staff">Mitarbeiter</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteUser(u.id)}
                                    data-testid={`delete-user-${u.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" strokeWidth={1.5} />
                <p className="text-slate-500">
                  Nur Administratoren können Benutzer verwalten
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* System Tab - Only for Admins */}
        {user?.role === "admin" && (
        <TabsContent value="system" className="space-y-4 sm:space-y-6">
          {/* Scheduler Section */}
          <Card className="border-slate-200">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Clock className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                Automatische Aufgaben
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Geplante Aufgaben und automatische Mietgenerierung
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
              {schedulerStatus && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${schedulerStatus.running ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm font-medium text-slate-700">
                        Scheduler {schedulerStatus.running ? 'läuft' : 'gestoppt'}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchSchedulerStatus}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {schedulerStatus.jobs?.length > 0 && (
                    <div className="space-y-2">
                      {schedulerStatus.jobs.map((job) => (
                        <div key={job.id} className="bg-white p-3 rounded border">
                          <p className="text-sm font-medium">{job.name}</p>
                          <p className="text-xs text-slate-500">
                            Nächste Ausführung: {job.next_run ? new Date(job.next_run).toLocaleString('de-DE') : 'Nicht geplant'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {schedulerStatus.last_auto_rent && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <p className="text-xs text-emerald-700">
                        <strong>Letzte automatische Miete:</strong> {schedulerStatus.last_auto_rent.month} - 
                        {schedulerStatus.last_auto_rent.generated_count} Mieten (€{schedulerStatus.last_auto_rent.total_amount?.toFixed(2)})
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              <Button
                variant="outline"
                onClick={handleTriggerAutoRent}
                disabled={triggeringRent}
                data-testid="trigger-auto-rent-btn"
              >
                {triggeringRent ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                {triggeringRent ? "Wird ausgeführt..." : "Mietgenerierung jetzt ausführen"}
              </Button>
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>Info:</strong> Die automatische Mietgenerierung läuft jeden 1. des Monats um 00:05 Uhr.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Delete Monthly Payments Section */}
          <Card className="border-slate-200">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                Monatliche Zahlungen löschen
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Fehlerhaft gebuchte Monatsmieten löschen
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
              {/* Summary of monthly payments */}
              {monthlyPaymentsSummary.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Gebuchte Monate</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchMonthlyPaymentsSummary}
                      disabled={loadingMonthlyPayments}
                    >
                      <RefreshCw className={`h-4 w-4 ${loadingMonthlyPayments ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto space-y-2">
                    {monthlyPaymentsSummary.map((item) => (
                      <div key={item.month} className="flex justify-between items-center bg-white p-2 rounded border text-sm">
                        <span className="font-medium">{item.month}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-slate-500">{item.count} Buchungen</span>
                          <span className="font-medium">€{item.total.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <Dialog open={deletePaymentsDialogOpen} onOpenChange={setDeletePaymentsDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Monatsmieten löschen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Monatliche Zahlungen löschen</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>Achtung:</strong> Diese Aktion löscht alle Mietzahlungen für den ausgewählten Monat aus den Konten und Zahlungen.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Monat auswählen</Label>
                      <Select value={deletePaymentsMonth} onValueChange={setDeletePaymentsMonth}>
                        <SelectTrigger>
                          <SelectValue placeholder="Monat wählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {monthlyPaymentsSummary.map((item) => (
                            <SelectItem key={item.month} value={item.month}>
                              {item.month} ({item.count} Buchungen - €{item.total.toFixed(2)})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button
                      className="w-full bg-red-600 hover:bg-red-700"
                      onClick={handleDeleteMonthlyPayments}
                      disabled={deletingPayments || !deletePaymentsMonth}
                    >
                      {deletingPayments ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird gelöscht...</>
                      ) : (
                        <><Trash2 className="h-4 w-4 mr-2" /> Zahlungen löschen</>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-800">
                  <strong>Warnung:</strong> Gelöschte Zahlungen können nicht wiederhergestellt werden.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Recalculate Month Rent Section */}
          <Card className="border-slate-200">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                Monatsmiete neu berechnen
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Fehlerhaft berechnete Monatsmieten korrigieren
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Info:</strong> Alle Auto-Mieten des gewählten Monats werden gelöscht und mit korrekten Beträgen neu erstellt.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Input
                  type="month"
                  value={recalcMonth}
                  onChange={(e) => setRecalcMonth(e.target.value)}
                  className="w-48"
                  data-testid="recalc-month-input"
                />
                <Button
                  onClick={handleRecalculateMonth}
                  disabled={recalculating || !recalcMonth}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="recalc-month-btn"
                >
                  {recalculating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird berechnet...</>
                  ) : (
                    <><RefreshCw className="h-4 w-4 mr-2" /> Neu berechnen</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Backup Section */}
          <Card className="border-slate-200">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <HardDrive className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "#c11718" }} />
                Datensicherung
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Systemdaten sichern und wiederherstellen
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
              {/* Backup Info */}
              {backupInfo && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Datenübersicht (Vollständiges Backup)</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchBackupInfo}
                      disabled={loadingBackupInfo}
                    >
                      <RefreshCw className={`h-4 w-4 ${loadingBackupInfo ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-slate-500">Bewohner</p>
                      <p className="text-base font-semibold">{backupInfo.collections?.guests || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-slate-500">Zimmer</p>
                      <p className="text-base font-semibold">{backupInfo.collections?.rooms || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-slate-500">Aufnahmen</p>
                      <p className="text-base font-semibold">{backupInfo.collections?.bookings || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-slate-500">Zahlungen</p>
                      <p className="text-base font-semibold">{backupInfo.collections?.payments || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-slate-500">Kontobuchungen</p>
                      <p className="text-base font-semibold">{backupInfo.collections?.account_transactions || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-slate-500">Kasse</p>
                      <p className="text-base font-semibold">{backupInfo.collections?.cash_transactions || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-slate-500">Benutzer</p>
                      <p className="text-base font-semibold">{backupInfo.collections?.users || 0}</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-slate-500">Aktivitäten</p>
                      <p className="text-base font-semibold">{backupInfo.collections?.activities || 0}</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-slate-700">
                      Gesamt: {backupInfo.total_documents || 0} Datensätze
                    </p>
                    <p className="text-xs text-slate-500">
                      15 Kollektionen werden gesichert
                    </p>
                  </div>
                </div>
              )}
              
              {/* Backup Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleCreateBackup}
                  disabled={creatingBackup}
                  className="flex-1"
                  style={{ backgroundColor: "#c11718" }}
                  data-testid="create-backup-btn"
                >
                  {creatingBackup ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  {creatingBackup ? "Backup wird erstellt..." : "Backup erstellen"}
                </Button>
                
                {/* Restore Button */}
                <div className="flex-1">
                  <Input
                    type="file"
                    accept=".zip"
                    onChange={handleRestoreFileSelect}
                    className="hidden"
                    id="restore-file-input"
                  />
                  <Label htmlFor="restore-file-input" className="cursor-pointer w-full">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      asChild
                      data-testid="restore-backup-btn"
                    >
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Backup wiederherstellen
                      </span>
                    </Button>
                  </Label>
                </div>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Tipp:</strong> Erstellen Sie regelmäßig Backups, um Datenverlust zu vermeiden. 
                  Das Backup enthält alle Bewohner, Zimmer, Buchungen und Kontobewegungen.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Reset Accounts Section */}
          <Card className="border-red-200">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-red-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Wallet className="h-4 w-4 sm:h-5 sm:w-5" />
                Konten zurücksetzen
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Alle Kontobewegungen (Transaktionen) löschen
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-800">
                  <strong>Warnung:</strong> Diese Aktion löscht alle Kontobewegungen (Mieten, Zahlungen) 
                  aller Bewohner unwiderruflich. Bewohner- und Zimmerdaten bleiben erhalten.
                </p>
              </div>
              
              <Button
                variant="outline"
                className="w-full sm:w-auto text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => setResetAccountsDialogOpen(true)}
                data-testid="reset-accounts-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Alle Konten zurücksetzen
              </Button>
            </CardContent>
          </Card>

          {/* Reset Bewohner Section */}
          <Card className="border-red-200">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-red-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <UserX className="h-4 w-4 sm:h-5 sm:w-5" />
                Bewohner zurücksetzen
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Alle Bewohner, Buchungen und Kontobewegungen löschen
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-800">
                  <strong>Warnung:</strong> Diese Aktion löscht ALLE Bewohner, Buchungen und Kontobewegungen 
                  unwiderruflich. Zimmer und Betten bleiben erhalten, werden aber als verfügbar markiert.
                </p>
              </div>
              
              <Button
                variant="outline"
                className="w-full sm:w-auto text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => setResetBewohnerDialogOpen(true)}
                data-testid="reset-bewohner-btn"
              >
                <UserX className="h-4 w-4 mr-2" />
                Alle Bewohner zurücksetzen
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Restore Confirmation Dialog */}
        <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">⚠️ Backup wiederherstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Achtung!</strong> Alle aktuellen Daten werden durch die Backup-Daten ersetzt. 
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
              {selectedRestoreFile && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <strong>Datei:</strong> {selectedRestoreFile.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    Größe: {(selectedRestoreFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setRestoreDialogOpen(false);
                    setSelectedRestoreFile(null);
                  }}
                  disabled={restoringBackup}
                >
                  Abbrechen
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleRestoreBackup}
                  disabled={restoringBackup}
                  data-testid="confirm-restore-btn"
                >
                  {restoringBackup ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Archive className="h-4 w-4 mr-2" />
                  )}
                  {restoringBackup ? "Wird wiederhergestellt..." : "Wiederherstellen"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Accounts Confirmation Dialog */}
        <Dialog open={resetAccountsDialogOpen} onOpenChange={(open) => {
          setResetAccountsDialogOpen(open);
          if (!open) {
            setResetAccountsPassword("");
            setResetAccountsMode("all");
            setResetAccountsGuestId("");
            setResetAccountsIncludePayments(true);
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Lock className="h-5 w-5" />
                Konten zurücksetzen
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Achtung!</strong> Kontobewegungen werden unwiderruflich gelöscht.
                </p>
              </div>
              
              {/* Mode Selection */}
              <div className="space-y-3">
                <Label>Zurücksetzen für</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="resetMode"
                      value="all"
                      checked={resetAccountsMode === "all"}
                      onChange={() => setResetAccountsMode("all")}
                      className="w-4 h-4 text-red-600"
                    />
                    <span className="text-sm">Alle Bewohner</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="resetMode"
                      value="single"
                      checked={resetAccountsMode === "single"}
                      onChange={() => setResetAccountsMode("single")}
                      className="w-4 h-4 text-red-600"
                    />
                    <span className="text-sm">Einzelner Bewohner</span>
                  </label>
                </div>
              </div>
              
              {/* Guest Selection (only for single mode) */}
              {resetAccountsMode === "single" && (
                <div className="space-y-2">
                  <Label>Bewohner auswählen</Label>
                  <select
                    value={resetAccountsGuestId}
                    onChange={(e) => setResetAccountsGuestId(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">-- Bewohner wählen --</option>
                    {guests.map((guest) => (
                      <option key={guest.id} value={guest.id}>
                        {guest.first_name} {guest.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Include Payments Checkbox */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={resetAccountsIncludePayments}
                    onChange={(e) => setResetAccountsIncludePayments(e.target.checked)}
                    className="w-4 h-4 mt-0.5 text-red-600 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium">Zahlungen auch löschen</span>
                    <p className="text-xs text-slate-500 mt-1">
                      Wenn aktiviert, werden auch alle Zahlungen (Mietzahlungen) gelöscht
                    </p>
                  </div>
                </label>
              </div>
              
              {/* Summary */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Zusammenfassung:</strong><br />
                  {resetAccountsMode === "all" ? "• Alle Bewohner" : `• Ausgewählter Bewohner`}<br />
                  • Kontobewegungen werden gelöscht<br />
                  {resetAccountsIncludePayments && "• Zahlungen werden gelöscht"}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reset-accounts-password">Admin-Passwort zur Bestätigung</Label>
                <Input
                  id="reset-accounts-password"
                  type="password"
                  value={resetAccountsPassword}
                  onChange={(e) => setResetAccountsPassword(e.target.value)}
                  placeholder="Passwort eingeben..."
                  data-testid="reset-accounts-password-input"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setResetAccountsDialogOpen(false);
                    setResetAccountsPassword("");
                    setResetAccountsMode("all");
                    setResetAccountsGuestId("");
                    setResetAccountsIncludePayments(true);
                  }}
                  disabled={resettingAccounts}
                >
                  Abbrechen
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleResetAccounts}
                  disabled={resettingAccounts || !resetAccountsPassword || (resetAccountsMode === "single" && !resetAccountsGuestId)}
                  data-testid="confirm-reset-accounts-btn"
                >
                  {resettingAccounts ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4 mr-2" />
                  )}
                  {resettingAccounts ? "Wird zurückgesetzt..." : "Zurücksetzen"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Bewohner Confirmation Dialog */}
        <Dialog open={resetBewohnerDialogOpen} onOpenChange={setResetBewohnerDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <UserX className="h-5 w-5" />
                Alle Bewohner zurücksetzen
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Achtung!</strong> ALLE Bewohner, Buchungen und Kontobewegungen 
                  werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reset-bewohner-password">Admin-Passwort zur Bestätigung</Label>
                <Input
                  id="reset-bewohner-password"
                  type="password"
                  value={resetBewohnerPassword}
                  onChange={(e) => setResetBewohnerPassword(e.target.value)}
                  placeholder="Passwort eingeben..."
                  data-testid="reset-bewohner-password-input"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setResetBewohnerDialogOpen(false);
                    setResetBewohnerPassword("");
                  }}
                  disabled={resettingBewohner}
                >
                  Abbrechen
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleResetBewohner}
                  disabled={resettingBewohner || !resetBewohnerPassword}
                  data-testid="confirm-reset-bewohner-btn"
                >
                  {resettingBewohner ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <UserX className="h-4 w-4 mr-2" />
                  )}
                  {resettingBewohner ? "Wird zurückgesetzt..." : "Zurücksetzen"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-4 sm:space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <svg className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3h18v18H3z" />
                  <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
                </svg>
                Bank (FinAPI)
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Automatische Zahlungserkennung
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs sm:text-sm text-blue-800">
                  <strong>Hinweis:</strong> FinAPI für Sparkasse Nürnberg ist noch nicht konfiguriert.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
